import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AIOptimizeDialog({ open, onOpenChange, query }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [context, setContext] = useState("");
  const [copied, setCopied] = useState(false);

  const handleOptimize = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/optimize-query", {
        sql_content: query.sql_content,
        dialect: "MSSQL",
        context: context || `Type: ${query.type}, Product: ${query.product}${query.project ? `, Project: ${query.project}` : ""}. Purpose: ${query.purpose || "N/A"}`,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.optimized_sql) return;
    try {
      await navigator.clipboard.writeText(result.optimized_sql);
      setCopied(true);
      toast.success("Optimized SQL copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* ignore */ }
  };

  const handleClose = (o) => {
    if (!o) {
      setResult(null);
      setContext("");
    }
    onOpenChange(o);
  };

  if (!query) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ai-optimize-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            AI Query Optimizer
          </DialogTitle>
          <p className="text-sm text-slate-500">Get an optimized version of your SQL with explanation.</p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Original SQL</div>
            <pre className="sql-code-block max-h-40 overflow-auto text-xs" data-testid="ai-original-sql">{query.sql_content}</pre>
          </div>

          {!result && !loading && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Additional Context (optional)</div>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="e.g., 'this is run every 5 minutes against a 50M row table'"
                rows={2}
                data-testid="ai-context-input"
              />
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm">Optimizing… this can take 5–15 seconds.</span>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Optimized SQL</div>
                  <Button
                    variant="outline" size="sm"
                    onClick={handleCopy}
                    className="h-7 text-xs"
                    data-testid="ai-copy-optimized-button"
                  >
                    {copied ? <Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <pre className="sql-code-block max-h-60 overflow-auto text-xs border-2 border-emerald-400" data-testid="ai-optimized-sql">{result.optimized_sql}</pre>
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5">Explanation</div>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm text-slate-700 whitespace-pre-wrap" data-testid="ai-explanation">
                  {result.explanation}
                </div>
              </div>
              <div className="text-[11px] text-slate-400 font-mono">Model: {result.model}</div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} data-testid="ai-close-button">Close</Button>
          {!result && (
            <Button
              onClick={handleOptimize}
              disabled={loading}
              className="bg-blue-700 hover:bg-blue-800 text-white"
              data-testid="ai-optimize-button"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {loading ? "Working…" : "Optimize with AI"}
            </Button>
          )}
          {result && (
            <Button
              onClick={() => { setResult(null); setContext(""); }}
              variant="outline"
              data-testid="ai-retry-button"
            >
              Try Again
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
